/**
 * Typed client for /api/admin/data-uploads — Admin → Data uploads.
 *
 * Mirrors hy_sales/schemas/data_uploads.py. Upload → review → confirm:
 * the API validates the file without loading anything, the uploader
 * confirms, and a Cloud Run job loads it all-or-nothing while this page
 * polls its progress.
 *
 * Needs the `data_upload` role itself — `admin` does not imply it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiDelete, apiGet, apiPost, apiPostForm } from './client';

export type CheckLevel = 'ok' | 'warn' | 'block';

export interface UploadCheck {
  level: CheckLevel;
  label: string;
  detail: string;
}

export interface MonthChange {
  /** ISO date, first of the month. */
  month: string;
  before: number;
  after: number;
  delta: number;
  pct?: number | null;
}

/** What validation found. Fields beyond `checks` are absent when the file was unreadable. */
export interface UploadPreview {
  source_system?: string;
  source_label?: string;
  first_month?: string;
  last_month?: string;
  data_through?: string | null;
  facts?: number;
  accounts?: number;
  products?: number;
  total_9l?: number;
  new_accounts?: number;
  new_products?: string[];
  month_changes?: MonthChange[];
  last_load?: { filename: string; data_through: string | null; facts: number } | null;
  checks: UploadCheck[];
  blocked: boolean;
  warnings: number;
}

export interface UploadProgress {
  step: string;
  done: number;
  total: number;
}

export interface TargetSnapshot {
  facts: number;
  accounts: number;
  total_9l: number;
}

export interface TargetResult {
  name: string;
  status: string;
  issues: string[];
  load?: { inserted: number; updated: number; unchanged: number; failed: number };
  before?: TargetSnapshot;
  after?: TargetSnapshot;
  month_changes?: MonthChange[];
}

export interface UploadResult {
  outcome: string;
  ok: boolean;
  issues?: string[];
  error?: string | null;
  failed_step?: string | null;
  data_through?: string | null;
  facts?: number | null;
  targets?: TargetResult[];
}

export type UploadStatus =
  | 'validated'
  | 'rejected'
  | 'queued'
  | 'running'
  | 'committed'
  | 'up_to_date'
  | 'rolled_back'
  | 'partial'
  | 'failed'
  | 'discarded'
  | 'expired';

export interface DataUpload {
  id: number;
  filename: string;
  size_bytes: number;
  source_system: string | null;
  status: UploadStatus;
  targets: string[];
  preview: UploadPreview;
  progress: UploadProgress | null;
  result: UploadResult | null;
  created_by_name: string | null;
  created_at: string;
  expires_at: string;
  confirmed_by_name: string | null;
  confirmed_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  can_confirm: boolean;
}

export interface DataUploadsResponse {
  targets: string[];
  max_mb: number;
  active: DataUpload | null;
  items: DataUpload[];
}

export const ACTIVE_STATUSES: UploadStatus[] = ['queued', 'running'];

const KEY = ['admin', 'data-uploads'] as const;

export function useDataUploads() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => apiGet<DataUploadsResponse>('sales', '/api/admin/data-uploads'),
    // Poll while a load runs so progress and the outcome appear on their own.
    refetchInterval: (query) => (query.state.data?.active ? 3000 : false),
    // Keep polling in a background tab too: a load is short-lived and the
    // page should show the finished result the moment someone looks.
    refetchIntervalInBackground: true,
    retry: false,
  });
}

export function useUploadFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return apiPostForm<DataUpload>('sales', '/api/admin/data-uploads', form);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useConfirmUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, acknowledgeWarnings }: { id: number; acknowledgeWarnings: boolean }) =>
      apiPost<DataUpload>('sales', `/api/admin/data-uploads/${id}/confirm`, {
        acknowledge_warnings: acknowledgeWarnings,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDiscardUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiPost<DataUpload>('sales', `/api/admin/data-uploads/${id}/discard`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ── Who gets the load email ──────────────────────────────────────
//
// The email is a data-free notice (loaded or not, where, when, by whom),
// so any address may be listed. The uploader always gets their own.

export interface NoticeRecipient {
  id: number;
  email: string;
  added_by_name: string | null;
  added_at: string;
}

export interface NoticeRecipientsResponse {
  items: NoticeRecipient[];
}

const RECIPIENTS_KEY = ['admin', 'data-uploads', 'recipients'] as const;

export function useNoticeRecipients() {
  return useQuery({
    queryKey: RECIPIENTS_KEY,
    queryFn: () => apiGet<NoticeRecipientsResponse>('sales', '/api/admin/data-uploads/recipients'),
    retry: false,
  });
}

export function useAddNoticeRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiPost<NoticeRecipientsResponse>('sales', '/api/admin/data-uploads/recipients', { email }),
    onSuccess: (data) => qc.setQueryData(RECIPIENTS_KEY, data),
  });
}

export function useRemoveNoticeRecipient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiDelete<NoticeRecipientsResponse>('sales', `/api/admin/data-uploads/recipients/${id}`),
    onSuccess: (data) => qc.setQueryData(RECIPIENTS_KEY, data),
  });
}
